import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/events")({
  head: () => ({
    meta: [
      { title: "Events · Buddha KA" },
      { name: "description", content: "Live performances, listening parties, livestreams, and workshops from Buddha KA." },
      { property: "og:title", content: "Events · Buddha KA" },
      { property: "og:description", content: "Live performances, listening parties, livestreams, and workshops." },
      { property: "og:url", content: absoluteUrl("/events") },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/events") }],
  }),
  component: EventsPage,
});

type EventRow = {
  id: string;
  slug: string;
  title: string;
  kind: string;
  description: string | null;
  starts_at: string;
  ends_at: string | null;
  timezone: string | null;
  location: string | null;
  stream_url: string | null;
  rsvp_url: string | null;
  image_url: string | null;
};

const KIND_LABEL: Record<string, string> = {
  live: "Live performance",
  listening_party: "Listening party",
  livestream: "Livestream",
  workshop: "Workshop",
};

function EventsPage() {
  const [events, setEvents] = useState<EventRow[] | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("events")
        .select("id, slug, title, kind, description, starts_at, ends_at, timezone, location, stream_url, rsvp_url, image_url")
        .eq("published", true)
        .gte("starts_at", new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString())
        .order("starts_at", { ascending: true });
      setEvents((data ?? []) as EventRow[]);
    })();
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 py-24">
      <header className="mb-16 text-center">
        <p className="text-xs uppercase tracking-[0.35em] text-gold">Gatherings</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl text-gold-luminous">Events</h1>
        <p className="mt-6 mx-auto max-w-2xl text-muted-foreground">
          Live performances, listening parties, livestreams, and workshops.
        </p>
      </header>

      {events === null ? (
        <p className="text-center text-sm text-muted-foreground">Loading…</p>
      ) : events.length === 0 ? (
        <div className="rounded-xl border hairline p-12 text-center">
          <p className="font-serif text-2xl">No events currently scheduled</p>
          <p className="mt-3 text-sm text-muted-foreground">Subscribe to the newsletter to be first to know.</p>
        </div>
      ) : (
        <ul className="space-y-6">
          {events.map((e) => (
            <li key={e.id} className="rounded-xl border hairline p-6 md:p-8 transition hover:border-gold">
              <div className="flex flex-col md:flex-row gap-6">
                {e.image_url && (
                  <img src={e.image_url} alt="" className="w-full md:w-48 aspect-video md:aspect-square rounded-lg object-cover" loading="lazy" />
                )}
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.25em] text-gold">{KIND_LABEL[e.kind] ?? e.kind}</p>
                  <h2 className="mt-2 font-serif text-3xl text-gold-luminous">{e.title}</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {new Date(e.starts_at).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
                    {e.timezone ? ` (${e.timezone})` : ""}
                  </p>
                  {e.location && <p className="mt-1 text-sm">{e.location}</p>}
                  {e.description && <p className="mt-4 text-sm whitespace-pre-wrap">{e.description}</p>}
                  <div className="mt-6 flex flex-wrap gap-3">
                    {e.rsvp_url && (
                      <a href={e.rsvp_url} target="_blank" rel="noopener noreferrer" className="rounded-full border hairline px-5 py-2 text-xs uppercase tracking-[0.2em] text-gold transition hover:bg-gold/10">
                        RSVP
                      </a>
                    )}
                    {e.stream_url && (
                      <a href={e.stream_url} target="_blank" rel="noopener noreferrer" className="rounded-full border hairline px-5 py-2 text-xs uppercase tracking-[0.2em] transition hover:border-gold">
                        Watch stream
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
